import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, NetworkError } from '@/lib/api.js';
import { useAuthStore } from '@/stores/authStore.js';
import { BrandMark, GlassButton } from '@/components/ui/index.js';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code === 'unauthorized' ? '用户名或密码不正确' : err.message);
      } else if (err instanceof NetworkError) {
        setError('网络异常，无法连接 API');
      } else {
        setError('登录失败');
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="page-surface relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
      <div className="relative w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <BrandMark
              emojiClassName="text-3xl"
              textClassName="font-display font-bold text-white tracking-tight text-xl"
            />
          </div>
          <p className="text-sm text-slate-400">需要 admin 账号登录</p>
        </div>
        <form
          onSubmit={(event): void => { void handleSubmit(event); }}
          className="mat-matte rounded-2xl p-6 space-y-5 shadow-xl shadow-black/30"
        >
          <div>
            <label htmlFor="username" className="block text-sm text-slate-300 mb-1.5">
              用户名
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-slate-200 focus:border-shark-500 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm text-slate-300 mb-1.5">
              密码
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-slate-200 focus:border-shark-500 focus:outline-none"
            />
          </div>
          {error !== null && (
            <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <GlassButton
            type="submit"
            variant="primary"
            size="lg"
            disabled={submitting}
            className="w-full"
          >
            {submitting ? '登录中…' : '登录'}
          </GlassButton>
        </form>
      </div>
    </div>
  );
}
